/**
 * The hall's layout: bay-and-chase, or ballroom.
 *
 * Two ways a cleanroom is built, and a student who trains in one will walk
 * into the other:
 *
 *   - BAY-AND-CHASE — the tool faces set in a bulkhead wall either side of
 *     the aisle, the mainframes and their plumbing in the chase behind it,
 *     a door and a window into the chase at every bay. The 200 mm-era
 *     layout, still how older, analog, power and MEMS fabs are built, and
 *     the one this hall was rebuilt around: the chase is a room you can
 *     walk into and follow a gas line from tool to header to wall.
 *   - BALLROOM — the same two rows of tools on one open floor, no wall,
 *     every mainframe in view from the aisle and the service side behind
 *     each row open to it. How a new 300 mm fab is built: once the FOUP and
 *     the front end put the Class 1 volume inside the tool, there was no
 *     reason left to wall the room up, and a wall-less floor can be re-laid
 *     without a builder.
 *
 * The same tools, the same physics, the same sub-fab and the same work
 * orders under both. What changes is the room.
 *
 * A LIVE BINDING, CHANGED IN PLACE. Half of what the builders place is
 * placed against the wall line, so a change of layout is a rebuild of the
 * hall - and that is what it is: the composition root builds a new FabScene
 * against the new value and swaps it under the same renderer, player,
 * people and sub-fab (main.tsx rebuildHall), rather than reloading the page
 * and paying the module load, the shader compile and the model fetch again.
 * Every builder reads `BALLROOM` inside a function, at build time, so an ES
 * live binding is enough for them to build the other hall; nothing reads it
 * at module level, and nothing should - that would freeze one hall into a
 * constant that outlives the switch.
 *
 * App-level like lighting and quality: remembered per machine, chosen from
 * the main menu, never part of a session.
 *
 * AND A THIRD KIND, which is not a third layout: a fab somebody BUILT. A
 * custom floor is one of these two halls with a chosen subset of the
 * twenty-five stations standing in it (fab/floorPlan.ts), so its id carries
 * the plan - `custom:9k2f1a` - and everything that keys off a hall id keeps
 * working without knowing there is a third kind. The saved place is per fab
 * because the key is the id; the loading screen names it because the id
 * finds the plan; the row is laid out for it because layout.ts reads the
 * plan the id names. One string, and no new concept anywhere downstream.
 */

import { activePlan, getPlan, setActivePlanId, type FloorPlan } from './floorPlan'

export type HallLayout = 'bayChase' | 'ballroom' | `custom:${string}`

const STORAGE_KEY = 'simfab.hall'

/** The plan id inside a custom hall's id, or null for the two authored halls. */
export const planIdOf = (layout: HallLayout): string | null =>
  layout.startsWith('custom:') ? layout.slice('custom:'.length) : null

/** The hall id a saved floor is stood in as. */
export const hallIdFor = (planId: string): HallLayout => `custom:${planId}`

function readStored(): HallLayout {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'bayChase' || v === 'ballroom') return v
    // A custom hall is only a hall while its plan is still saved. A student
    // who deletes the floor they last stood in comes back to Fab A rather
    // than to an empty room with no tools and no way out of it.
    if (typeof v === 'string' && v.startsWith('custom:') && getPlan(v.slice('custom:'.length))) {
      return v as HallLayout
    }
  } catch {
    // Storage blocked - the default is fine.
  }
  return 'bayChase'
}

/** The layout the hall is built with right now. */
export let HALL_LAYOUT: HallLayout = readStored()

/**
 * Whether THIS hall is walled or open - which for a custom floor is the
 * plan's own choice, not a third value. Recomputed on every switch rather
 * than derived at each read, because every builder reads it as a live
 * binding at build time and a function call is not what they are reading.
 */
function ballroomFor(layout: HallLayout): boolean {
  if (layout === 'ballroom') return true
  const planId = planIdOf(layout)
  return planId ? getPlan(planId)?.hall === 'ballroom' : false
}

/** True when the bulkhead is down and the tool rows stand in the open. */
export let BALLROOM = ballroomFor(HALL_LAYOUT)

const listeners = new Set<(to: HallLayout, from: HallLayout) => void>()

/** Told after the binding has flipped, with both halls, so the caller can rebuild and hand over. */
export function onHallChange(fn: (to: HallLayout, from: HallLayout) => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function currentHall(): HallLayout {
  return HALL_LAYOUT
}

/**
 * Choose a layout. Stores it, flips the binding, and tells the composition
 * root, which rebuilds the hall under the running session - no reload.
 */
export function setHall(wanted: HallLayout): void {
  // A custom hall whose plan no longer exists - deleted in another tab, or
  // a menu drawn before the delete - is not a hall. Fab A, rather than a
  // Fab A-shaped room filed under a dead id with no tick beside it.
  const stale = planIdOf(wanted) !== null && !getPlan(planIdOf(wanted)!)
  const id: HallLayout = stale ? 'bayChase' : wanted
  if (id === HALL_LAYOUT) return
  const from = HALL_LAYOUT
  // The plan is pointed at BEFORE the binding flips, so layout.ts - the
  // first listener, and the one that lays the row out - reads the plan this
  // hall is for rather than the one the last hall was for.
  setActivePlanId(planIdOf(id))
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Not remembered across pages; still switched on this one.
  }
  HALL_LAYOUT = id
  BALLROOM = ballroomFor(id)
  for (const fn of [...listeners]) fn(id, from)
}

/**
 * Rebuild the hall around the floor plan as it now stands.
 *
 * For the author who has just changed their own floor while standing in it:
 * the hall id has not changed, so `setHall` sees no switch and does nothing,
 * but the row, the walls and the length all have to be laid out again. Same
 * listeners, same rebuild, `from` and `to` deliberately equal - main.tsx
 * saves the place under `from` and restores it under `to`, and for a re-lay
 * those are the same fab, which is exactly right.
 *
 * A no-op when the hall standing is not this plan's, so saving from the
 * authoring dialog never yanks somebody out of Fab A.
 */
export function refreshCustomHall(planId: string): void {
  if (HALL_LAYOUT !== hallIdFor(planId)) return
  BALLROOM = ballroomFor(HALL_LAYOUT)
  for (const fn of [...listeners]) fn(HALL_LAYOUT, HALL_LAYOUT)
}

/** The plan the hall standing right now was built from, if it is a custom one. */
export const currentPlan = (): FloorPlan | null =>
  planIdOf(HALL_LAYOUT) ? activePlan() : null
